import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  Handle,
  MarkerType,
  Position,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Card, Empty, Spin, Typography, Tag, Space, Tooltip } from 'antd';
import {
  ArrowDownOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ContainerOutlined,
  FlagOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { workflowSourceView } from './workflowSource';
import { contextRoles, edgeBetween, linearOrder, plural, ranks, runOrder } from './workflowMapLayout';
import { getWorkflowDiagram, type WorkflowDiagram as Diagram, type WorkflowDiagramNode } from '@/api/workflowDiagram';
import type { WorkflowCatalogContext } from '@/api/types';
import type { WorkflowProgressState } from '@/realtime/useWorkflowProgress';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowDiagram — the map of a workflow as it runs
// ─────────────────────────────────────────────────────────────────────────────
//
// Reads GET api/AIProcessLibrary/Diagram (WorkflowDiagramService). What a reader needs from a workflow, in order:
//   • what starts it, and what the first step is handed;
//   • each step: AI or plain code, what it does, the agent it runs (name, model, skills, knowledge, briefings,
//     tools), and the rules it checks;
//   • what travels along each arrow, in plain words;
//   • the CONTEXT handed from step to step through a WorkflowStateSlot — usually around the agent step, which never
//     sees it. Drawn beside the steps, because no arrow can show it;
//   • what a finished run leaves behind.
//
// A straight-line workflow (most of them) renders as a column of cards in normal page flow — nothing to pan or
// zoom, nothing to overlap. A branching workflow renders on a React Flow canvas laid out by MEASURED card heights:
// the old canvas spaced rows a fixed 130px apart while cards were taller, so every card sat on the next one and hid
// the arrows and their labels (2026-10-10).
//
// Optional `progress` (useWorkflowProgress) lights each step as a run moves through it.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  workflowName: string;
  /** When provided, each step shows done / running / failed as the run progresses. */
  progress?: WorkflowProgressState;
  isDark?: boolean;
  /** Canvas height for a branching workflow. Defaults to 560. */
  height?: number;
}

type LiveStatus = 'completed' | 'active' | 'failed' | 'pending';

export function WorkflowDiagram({ workflowName, progress, isDark = false, height = 560 }: Props) {
  const [diagram, setDiagram] = useState<Diagram | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const data = await getWorkflowDiagram(workflowName);
        if (cancelled) return;
        if (!data) {
          setError('No diagram available: no database row and no workflow defined in code has this name.');
        } else {
          setDiagram(data);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to load workflow diagram.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workflowName]);

  const view = useMemo(() => {
    if (!diagram) return null;
    const order = runOrder(diagram);
    return {
      linear: linearOrder(diagram),
      numberOf: new Map(order.map((id, i) => [id, i + 1])),
      roles: contextRoles(diagram.contexts),
      live: computeLiveStatuses(diagram, progress),
    };
  }, [diagram, progress]);

  if (loading) {
    return (
      <Card>
        <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin />
        </div>
      </Card>
    );
  }
  if (error) {
    return (
      <Card>
        <Empty description={<Text type="secondary">{error}</Text>} />
      </Card>
    );
  }
  if (!diagram || !view) return null;

  const theme = themeOf(isDark);
  const contexts = diagram.contexts ?? [];
  const source = workflowSourceView(diagram.source);

  const flow = view.linear ? (
    <LinearFlow diagram={diagram} order={view.linear} view={view} theme={theme} />
  ) : (
    <ReactFlowProvider>
      <BranchingCanvas diagram={diagram} view={view} theme={theme} isDark={isDark} height={height} />
    </ReactFlowProvider>
  );

  return (
    <Card
      title={
        <Space>
          <Text strong>{diagram.title}</Text>
          <Tooltip title={source.explain}>
            <Tag style={{ fontSize: 11 }}>{source.label}</Tag>
          </Tooltip>
        </Space>
      }
      styles={{ body: { padding: 20 } }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: contexts.length > 0 ? 'minmax(0, 1fr) minmax(220px, 300px)' : 'minmax(0, 1fr)',
          gap: 20,
          alignItems: 'start',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <StartRow diagram={diagram} theme={theme} />
          {view.linear && view.linear.length > 0 && <Hop label={diagram.startReceives} theme={theme} />}
          {!view.linear && <div style={{ height: 12 }} />}
          {flow}
          {(diagram.results?.length ?? 0) > 0 && (
            <>
              <Hop label="When it finishes" theme={theme} />
              <ResultsRow results={diagram.results ?? []} theme={theme} />
            </>
          )}
        </div>
        {contexts.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {contexts.map((ctx) => (
              <ContextPanel key={ctx.key} ctx={ctx} diagram={diagram} numberOf={view.numberOf} theme={theme} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Theme — one palette per mode, read by every piece below
// ────────────────────────────────────────────────────────────────────────

interface Theme {
  isDark: boolean;
  text: string;
  muted: string;
  line: string;
  codeBg: string;
  codeBorder: string;
  aiBg: string;
  aiBorder: string;
  aiText: string;
  ctxBg: string;
  ctxBorder: string;
  ctxText: string;
  chipBg: string;
  chipBorder: string;
  ok: string;
}

function themeOf(isDark: boolean): Theme {
  return isDark
    ? {
        isDark,
        text: colors.slate[50],
        muted: colors.slate[400],
        line: colors.slate[600],
        codeBg: colors.slate[800],
        codeBorder: colors.slate[700],
        aiBg: colors.violet[700] + '33',
        aiBorder: colors.violet[500] + '99',
        aiText: colors.violet[100],
        ctxBg: colors.ink[700] + '22',
        ctxBorder: colors.ink[500] + '99',
        ctxText: colors.ink[100],
        chipBg: colors.slate[900],
        chipBorder: colors.slate[700],
        ok: colors.green[500],
      }
    : {
        isDark,
        text: colors.slate[900],
        muted: colors.slate[500],
        line: colors.slate[300],
        codeBg: '#FFFFFF',
        codeBorder: colors.slate[200],
        aiBg: colors.violet[50],
        aiBorder: colors.violet[200],
        aiText: colors.violet[700],
        ctxBg: colors.ink[50],
        ctxBorder: colors.ink[500] + '88',
        ctxText: colors.ink[700],
        chipBg: colors.slate[50],
        chipBorder: colors.slate[200],
        ok: colors.green[600],
      };
}

// ────────────────────────────────────────────────────────────────────────
// Straight-line workflows — a column of cards in page flow
// ────────────────────────────────────────────────────────────────────────

interface View {
  numberOf: Map<string, number>;
  roles: ReturnType<typeof contextRoles>;
  live: Map<string, LiveStatus>;
}

function LinearFlow({ diagram, order, view, theme }: { diagram: Diagram; order: string[]; view: View; theme: Theme }) {
  const byId = new Map(diagram.nodes.map((n) => [n.id, n]));
  return (
    <div>
      {order.map((id, i) => {
        const node = byId.get(id);
        if (!node) return null;
        const next = order[i + 1];
        return (
          <div key={id}>
            <StepCard node={node} number={view.numberOf.get(id) ?? i + 1} roles={view.roles.get(id)} live={view.live.get(id)} theme={theme} />
            {next && <Hop label={edgeBetween(diagram.edges, id, next)?.label} theme={theme} />}
          </div>
        );
      })}
    </div>
  );
}

function Hop({ label, theme }: { label?: string | null; theme: Theme }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginLeft: 22,
        minHeight: 40,
        padding: '6px 0 6px 14px',
        borderLeft: `2px solid ${theme.line}`,
        fontSize: 12,
        color: theme.muted,
      }}
    >
      <ArrowDownOutlined style={{ fontSize: 11 }} />
      {label && <span>{label}</span>}
    </div>
  );
}

function StartRow({ diagram, theme }: { diagram: Diagram; theme: Theme }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: theme.text }}>
      <PlayCircleOutlined style={{ fontSize: 18, color: theme.muted }} />
      <span style={{ fontWeight: 600, fontSize: 13 }}>{diagram.startsWhen || 'Start'}</span>
    </div>
  );
}

function ResultsRow({ results, theme }: { results: string[]; theme: Theme }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {results.map((r) => (
        <span key={r} style={{ ...chipStyle(theme), fontSize: 12, padding: '4px 10px' }}>
          <FlagOutlined style={{ marginRight: 6, color: theme.muted }} />
          {r}
        </span>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// One step
// ────────────────────────────────────────────────────────────────────────

function StepCard({
  node,
  number,
  roles,
  live,
  theme,
}: {
  node: WorkflowDiagramNode;
  number: number;
  roles?: { writes: WorkflowCatalogContext[]; reads: WorkflowCatalogContext[] };
  live?: LiveStatus;
  theme: Theme;
}) {
  const ai = node.isAgentNode;
  const fg = ai ? theme.aiText : theme.text;
  const sub = ai ? theme.aiText : theme.muted;
  const border =
    live === 'completed' ? colors.green[500] : live === 'active' ? colors.ink[500] : live === 'failed' ? colors.red[500] : ai ? theme.aiBorder : theme.codeBorder;
  const agent = node.agent;
  const checks = node.checks ?? [];

  return (
    <div
      style={{
        background: ai ? theme.aiBg : theme.codeBg,
        border: `${live && live !== 'pending' ? 2 : 1}px solid ${border}`,
        borderRadius: 12,
        padding: '12px 14px',
        boxShadow: live === 'active' ? `0 0 0 4px ${colors.ink[100]}` : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 24,
            height: 24,
            flex: 'none',
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            background: ai ? (theme.isDark ? colors.violet[700] + '55' : colors.violet[100]) : theme.chipBg,
            color: fg,
            border: `1px solid ${ai ? theme.aiBorder : theme.chipBorder}`,
          }}
        >
          {number}
        </span>
        <Tooltip title={node.classId}>
          <span style={{ fontWeight: 600, fontSize: 14, color: fg, minWidth: 0 }}>{node.title}</span>
        </Tooltip>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <LiveBadge live={live} />
          <span
            style={{
              fontSize: 11,
              whiteSpace: 'nowrap',
              padding: '1px 8px',
              borderRadius: 999,
              border: `1px solid ${ai ? theme.aiBorder : theme.chipBorder}`,
              color: ai ? theme.aiText : theme.muted,
            }}
          >
            {ai ? 'AI agent' : 'Code, no AI'}
          </span>
        </span>
      </div>

      <div style={{ marginLeft: 34 }}>
        {node.description && (
          <div style={{ marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: sub }}>{node.description}</div>
        )}

        {ai && <AgentLine node={node} theme={theme} />}
        {ai && agent && <AgentLoadout agent={agent} theme={theme} />}

        {checks.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {checks.map((c) => (
              <span key={c} style={{ fontSize: 12.5, color: sub }}>
                <CheckOutlined style={{ color: theme.ok, marginRight: 6 }} />
                {c}
              </span>
            ))}
          </div>
        )}

        {roles && (roles.writes.length > 0 || roles.reads.length > 0) && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {roles.writes.map((c) => (
              <ContextChip key={`w-${c.key}`} theme={theme}>
                puts the {c.title.toLowerCase()}
              </ContextChip>
            ))}
            {roles.reads.map((c) => (
              <ContextChip key={`r-${c.key}`} theme={theme}>
                takes the {c.title.toLowerCase()}
              </ContextChip>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AgentLine({ node, theme }: { node: WorkflowDiagramNode; theme: Theme }) {
  const agent = node.agent;
  const name = agent?.name || node.agentName || node.agentRefId || 'Agent';
  const model = agent?.modelName || node.agentModel;
  return (
    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13, color: theme.aiText }}>
      <RobotOutlined />
      {agent && agent.agentId > 0 ? (
        <Link to={`/agents/${agent.agentId}`} style={{ fontWeight: 600, color: theme.aiText }}>
          {name}
        </Link>
      ) : (
        <span style={{ fontWeight: 600 }}>{name}</span>
      )}
      {model ? <span>{model}</span> : <span style={{ fontStyle: 'italic' }}>model chosen when the run starts</span>}
    </div>
  );
}

function AgentLoadout({ agent, theme }: { agent: NonNullable<WorkflowDiagramNode['agent']>; theme: Theme }) {
  const tools = agent.tools ?? [];
  const items: ReactNode[] = [];
  if (agent.skillCount != null) items.push(plural(agent.skillCount, 'skill'));
  if (agent.knowledgeCount != null) items.push(plural(agent.knowledgeCount, 'knowledge base'));
  if (agent.briefingCount != null) items.push(plural(agent.briefingCount, 'briefing'));
  items.push(
    tools.length === 0 ? (
      'No tools'
    ) : (
      <Tooltip title={tools.join(', ')}>
        <span>{plural(tools.length, 'tool')}</span>
      </Tooltip>
    ),
  );
  return (
    <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {items.map((item, i) => (
        <span key={i} style={chipStyle(theme)}>
          {item}
        </span>
      ))}
    </div>
  );
}

function ContextChip({ children, theme }: { children: ReactNode; theme: Theme }) {
  return (
    <span
      style={{
        fontSize: 11.5,
        padding: '2px 8px',
        borderRadius: 6,
        background: theme.ctxBg,
        color: theme.ctxText,
        border: `1px dashed ${theme.ctxBorder}`,
      }}
    >
      <ContainerOutlined style={{ marginRight: 5 }} />
      {children}
    </span>
  );
}

function LiveBadge({ live }: { live?: LiveStatus }) {
  if (live === 'completed') return <CheckCircleFilled style={{ color: colors.green[600] }} />;
  if (live === 'active') return <LoadingOutlined spin style={{ color: colors.ink[500] }} />;
  if (live === 'failed') return <WarningFilled style={{ color: colors.red[500] }} />;
  return null;
}

function chipStyle(theme: Theme): CSSProperties {
  return {
    fontSize: 11.5,
    padding: '2px 8px',
    borderRadius: 6,
    background: theme.chipBg,
    color: theme.isDark ? colors.slate[200] : colors.slate[600],
    border: `1px solid ${theme.chipBorder}`,
  };
}

// ────────────────────────────────────────────────────────────────────────
// The context panel — what travels between steps, around the arrows
// ────────────────────────────────────────────────────────────────────────

function ContextPanel({
  ctx,
  diagram,
  numberOf,
  theme,
}: {
  ctx: WorkflowCatalogContext;
  diagram: Diagram;
  numberOf: Map<string, number>;
  theme: Theme;
}) {
  const stepName = (id: string) => {
    const n = diagram.nodes.find((x) => x.id === id);
    return `step ${numberOf.get(id) ?? '?'}${n ? ` (${n.title})` : ''}`;
  };
  const written = ctx.writtenBy.map(stepName).join(', ');
  const read = ctx.readBy.map(stepName).join(', ');

  return (
    <div style={{ border: `1px dashed ${theme.ctxBorder}`, borderRadius: 12, padding: '12px 14px', background: theme.ctxBg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: theme.ctxText }}>
        <ContainerOutlined style={{ fontSize: 16 }} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>{ctx.title}</span>
      </div>
      <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: theme.muted }}>
        {written ? `Written by ${written}.` : 'No step in this workflow writes it.'}{' '}
        {read ? `Read by ${read}.` : 'No step declares that it reads it.'}
        {ctx.carriedAroundAgent && ' The AI step in between never sees it.'}
      </div>
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ctx.parts.map((p) => (
          <div key={p.name}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{p.name}</div>
            {p.description && <div style={{ fontSize: 12, color: theme.muted }}>{p.description}</div>}
          </div>
        ))}
      </div>
      {ctx.summary && (
        <div
          style={{
            marginTop: 12,
            fontSize: 12,
            lineHeight: 1.5,
            color: theme.muted,
            borderTop: `1px solid ${theme.ctxBorder}`,
            paddingTop: 10,
          }}
        >
          {ctx.summary}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Branching workflows — React Flow canvas, laid out by measured card heights
// ────────────────────────────────────────────────────────────────────────

interface StepNodeData {
  node: WorkflowDiagramNode;
  number: number;
  roles?: { writes: WorkflowCatalogContext[]; reads: WorkflowCatalogContext[] };
  live?: LiveStatus;
  theme: Theme;
  [key: string]: unknown;
}

const CARD_WIDTH = 320;
const COLUMN_GAP = 48;
const ROW_GAP = 72; // room for the arrow and its label between rows
const ESTIMATED_HEIGHT = 200;

function StepNode({ data }: NodeProps<Node<StepNodeData>>) {
  // Handles anchor the edges; the canvas is read-only so they are invisible.
  const hidden: CSSProperties = { background: 'transparent', border: 'none', width: 1, height: 1 };
  return (
    <div style={{ width: CARD_WIDTH }}>
      <Handle type="target" position={Position.Top} isConnectable={false} style={hidden} />
      <StepCard node={data.node} number={data.number} roles={data.roles} live={data.live} theme={data.theme} />
      <Handle type="source" position={Position.Bottom} isConnectable={false} style={hidden} />
    </div>
  );
}

const NODE_TYPES = { step: StepNode };

/** Positions every node: rows by rank, each row as tall as its tallest card plus the gap for the arrow label. */
function positions(diagram: Diagram, heightOf: (id: string) => number): Map<string, { x: number; y: number }> {
  const rank = ranks(diagram);
  const rows = new Map<number, string[]>();
  for (const n of diagram.nodes) {
    const r = rank.get(n.id) ?? 0;
    rows.set(r, [...(rows.get(r) ?? []), n.id]);
  }
  const out = new Map<string, { x: number; y: number }>();
  let y = 0;
  for (const r of [...rows.keys()].sort((a, b) => a - b)) {
    const ids = rows.get(r)!;
    const width = ids.length * CARD_WIDTH + (ids.length - 1) * COLUMN_GAP;
    ids.forEach((id, i) => out.set(id, { x: -width / 2 + i * (CARD_WIDTH + COLUMN_GAP), y }));
    y += Math.max(...ids.map(heightOf)) + ROW_GAP;
  }
  return out;
}

function BranchingCanvas({
  diagram,
  view,
  theme,
  isDark,
  height,
}: {
  diagram: Diagram;
  view: View;
  theme: Theme;
  isDark: boolean;
  height: number;
}) {
  const initialNodes = useMemo<Node<StepNodeData>[]>(() => {
    const pos = positions(diagram, () => ESTIMATED_HEIGHT);
    return diagram.nodes.map((n) => ({
      id: n.id,
      type: 'step',
      position: pos.get(n.id) ?? { x: 0, y: 0 },
      data: { node: n, number: view.numberOf.get(n.id) ?? 0, roles: view.roles.get(n.id), live: view.live.get(n.id), theme },
    }));
  }, [diagram, view, theme]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<StepNodeData>>(initialNodes);
  const measured = useNodesInitialized();
  const flow = useReactFlow();

  useEffect(() => setNodes(initialNodes), [initialNodes, setNodes]);

  // Second pass: once every card has rendered, re-space the rows by the cards' real heights.
  useEffect(() => {
    if (!measured) return;
    const heights = new Map(flow.getNodes().map((n) => [n.id, n.measured?.height ?? ESTIMATED_HEIGHT]));
    const pos = positions(diagram, (id) => heights.get(id) ?? ESTIMATED_HEIGHT);
    setNodes((current) => current.map((n) => ({ ...n, position: pos.get(n.id) ?? n.position })));
    requestAnimationFrame(() => void flow.fitView({ padding: 0.15 }));
  }, [measured, diagram, flow, setNodes]);

  const edges = useMemo<Edge[]>(() => {
    const stroke = isDark ? colors.slate[400] : colors.slate[500];
    return diagram.edges.map((e, i) => {
      const live = view.live.get(e.source) === 'completed' && view.live.get(e.target) === 'active';
      return {
        id: `${e.source}->${e.target}-${i}`,
        source: e.source,
        target: e.target,
        type: 'smoothstep',
        label: e.edgeKind === 'CONDITIONAL' ? `if… ${e.label ?? ''}`.trim() : e.label ?? undefined,
        labelStyle: { fontSize: 11, fill: isDark ? colors.slate[200] : colors.slate[600] },
        labelBgStyle: { fill: isDark ? colors.slate[800] : '#FFFFFF', fillOpacity: 0.95 },
        labelBgPadding: [6, 3] as [number, number],
        style: { stroke, strokeWidth: 2, strokeDasharray: e.edgeKind === 'CONDITIONAL' ? '6 4' : undefined },
        markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: 16, height: 16 },
        animated: live,
      };
    });
  }, [diagram, view, isDark]);

  return (
    <div style={{ height, border: `1px solid ${theme.codeBorder}`, borderRadius: 12, overflow: 'hidden' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable={false}
        minZoom={0.2}
      >
        <Background gap={16} color={isDark ? colors.slate[700] : colors.slate[200]} />
        <Controls position="bottom-right" showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Map progress state → per-node status
// ────────────────────────────────────────────────────────────────────────

function computeLiveStatuses(diagram: Diagram, progress: WorkflowProgressState | undefined): Map<string, LiveStatus> {
  const m = new Map<string, LiveStatus>();
  if (!progress) return m;
  for (const n of diagram.nodes) {
    if (progress.completedSteps.has(n.id)) m.set(n.id, 'completed');
    else if (progress.activeStepId === n.id) m.set(n.id, progress.status === 'failed' ? 'failed' : 'active');
    else m.set(n.id, 'pending');
  }
  return m;
}
